/**
 * The conflict ladder (M5 spec §8.1-§8.2; plan C9), day slice 3 (`DAY_SLICE.relations`).
 *
 * - **Pressure** per non-outlaw pair `(a, b)`, 0..`pressure.max` (ladder.json): + `perContest` per system
 *   contested between them (`contestPair`); + `claimFlip` per claim change between them today (`claimChanged`
 *   in the notable ring since the faction day began, so fine and coarse steps count the same flips); + `perKill`
 *   per ship one destroyed of the other's
 *   (`recordKill`, capped at `killCapPerDay`; not while at war when `killsAtWar` is false, R2-T4); + `conflictMult` × their mean `conflict` trait while any contest
 *   exists; + `lowTreasury` while either treasury is below `lowTreasuryShare` of its start; + `weariness` × war
 *   weariness while at war; − `decayPerDay`; − `warDecayPerDay` more while at war (balance pass T1, D-314, so wars
 *   end). `press`, `stoke` and zone wins add pressure at once (`addPressure`). A war's end stamps `warEndDay` (the
 *   `press.truceDays` truce, R2-T4 part 3) and settles the pressure at `afterWar`.
 * - **War weariness** (0..1): the larger of each side's war losses to the other ÷ its treasury.
 * - **Relation** with hysteresis (`relationFor`): up a level at ≥ `up`, down below `down`; war also needs both
 *   treasuries ≥ `war.minTreasury` and a contested system between them, and lasts at least `war.minDays`.
 * - **Grievance terms** (M7 spec §12.3): after the decay, an active term keeps `P(a,b) ≥ floor` (`story/grievance.ts`);
 *   the relation still follows the hysteresis and the war conditions below.
 * - **The referee** (`ladder.referee`, the Authority) and the outlaws never enter the ladder.
 * - **Economic effects** (read by C11's market path): in a system contested between a pair at economic or
 *   worse, the aggressor's stations undercut (`undercutMult`) and the pair deny each other supply (`denies`).
 *   The aggressor is the pair member that does not hold the claim; when neither holds it, both are.
 *
 * Relation columns are symmetric: every writer here keeps `x[a × fc + b]` and `x[b × fc + a]` equal.
 * Allocation-free (D-014): module scratch sized once per faction capacity.
 */
import { canScore } from '../claims/metrics'
import { emit, eventSlot, firstReadable } from '../events'
import { grievanceFloor } from '../story/grievance'
import { EV, RELATION, SIDE, TRAIT, TRAIT_COUNT, type StrategicState } from '../types'

/** Called when a pair leaves war (C9's `war.ts` passes `captureAtWarEnd`). A module-level function, never a closure. */
export type WarEndHook = (state: StrategicState, a: number, b: number) => void

/**
 * M10 P4a3a: called when a pair enters war, before `relationChanged` is emitted (`war.ts` passes `cancelWarOrders`).
 * Its return is the event's `qty` (the queued orders cancelled; 0 without a hook). A module-level function, never a
 * closure, passed in so `ladder.ts` gains no import of `shipyard.ts` (no module cycle).
 */
export type WarStartHook = (state: StrategicState, a: number, b: number) => number

let contests = new Uint16Array(0)
let flips = new Uint16Array(0)

function scratch(fc: number): void {
  const n = fc * fc
  if (contests.length !== n) {
    contests = new Uint16Array(n)
    flips = new Uint16Array(n)
  }
}

/** The referee faction index (ladder.json `referee`). */
export function refereeOf(state: StrategicState): number {
  return state.data.factions.factions.findIndex((f) => f.id === state.data.charter.ladder.referee)
}

let refereeCache: { data: unknown; index: number } = { data: null, index: -1 }

function referee(state: StrategicState): number {
  if (refereeCache.data !== state.data) refereeCache = { data: state.data, index: refereeOf(state) }
  return refereeCache.index
}

/** True when `f` takes part in the ladder: a non-outlaw faction other than the referee. */
export function inLadder(state: StrategicState, f: number): boolean {
  return canScore(state, f) && f !== referee(state)
}

/** The relation between `a` and `b` (RELATION code; peace for the same faction or out-of-range indices). */
export function relationOf(state: StrategicState, a: number, b: number): number {
  const rel = state.relations
  const fc = rel.factionCapacity
  if (a === b || a < 0 || b < 0 || a >= fc || b >= fc) return RELATION.peace
  return rel.relation[a * fc + b]
}

/** The pressure between `a` and `b`. */
export function pressureOf(state: StrategicState, a: number, b: number): number {
  const rel = state.relations
  const fc = rel.factionCapacity
  if (a === b || a < 0 || b < 0 || a >= fc || b >= fc) return 0
  return rel.pressure[a * fc + b]
}

function setPair(arr: Float32Array | Uint8Array | Int32Array, fc: number, a: number, b: number, v: number): void {
  arr[a * fc + b] = v
  arr[b * fc + a] = v
}

/** Adds `amount` pressure to the pair (clamped 0..max); no-op for a pair outside the ladder. `press`, `stoke` and zone wins. */
export function addPressure(state: StrategicState, a: number, b: number, amount: number): void {
  if (a === b || !inLadder(state, a) || !inLadder(state, b)) return
  const rel = state.relations
  const max = state.data.charter.ladder.pressure.max
  let p = rel.pressure[a * rel.factionCapacity + b] + amount
  if (p < 0) p = 0
  if (p > max) p = max
  setPair(rel.pressure, rel.factionCapacity, a, b, p)
}

/**
 * Records a ship of `victim` destroyed by `killer` (C11 calls it from the agent-lost hook): today's kill count
 * (pressure) and, while they are at war, the victim's war losses (weariness).
 */
export function recordKill(state: StrategicState, killer: number, victim: number, lossValue: number): void {
  const rel = state.relations
  const fc = rel.factionCapacity
  if (killer === victim || killer < 0 || victim < 0 || killer >= fc || victim >= fc) return
  const k = killer * fc + victim
  if (rel.killsToday[k] < 0xffff) rel.killsToday[k]++
  if (rel.relation[k] === RELATION.war && lossValue > 0) rel.warLossValue[victim * fc + killer] += lossValue
}

// ---------------------------------------------------------------------------------------------
// Contests
// ---------------------------------------------------------------------------------------------

let pairA = -1
let pairB = -1

/**
 * The pair contesting `sys` (claims.json `holder.contestedMin` / `contestedGap` over the last scores): the top
 * two scoring non-outlaw factions when both are at S ≥ contestedMin with a gap under contestedGap. Returns true
 * and keeps the pair (lower index first) for `contestA()` / `contestB()`.
 */
export function contestPair(state: StrategicState, sys: number): boolean {
  pairA = -1
  pairB = -1
  const cl = state.claims
  if (sys < 0 || sys >= cl.systems) return false
  const hc = state.data.charter.claims.holder
  let a = -1
  let sa = 0
  let b = -1
  let sb = 0
  const base = sys * cl.factionCapacity
  for (let f = 0; f < cl.factionCapacity; f++) {
    if (!canScore(state, f)) continue
    const s = cl.score[base + f]
    if (s > sa) {
      b = a
      sb = sa
      a = f
      sa = s
    } else if (s > sb) {
      b = f
      sb = s
    }
  }
  if (a < 0 || b < 0 || sb < hc.contestedMin || sa - sb >= hc.contestedGap) return false
  pairA = a < b ? a : b
  pairB = a < b ? b : a
  return true
}

/** The lower faction index of the last `contestPair` (−1 = none). */
export function contestA(): number {
  return pairA
}

/** The higher faction index of the last `contestPair` (−1 = none). */
export function contestB(): number {
  return pairB
}

/** True when `sys` is contested between `a` and `b`. */
export function contestedBetween(state: StrategicState, sys: number, a: number, b: number): boolean {
  if (!contestPair(state, sys)) return false
  return (pairA === a && pairB === b) || (pairA === b && pairB === a)
}

// ---------------------------------------------------------------------------------------------
// Economic effects (M5 spec §8.2)
// ---------------------------------------------------------------------------------------------

/**
 * The rival of `faction` in `sys` when the system is contested between them and their relation is economic
 * or worse, else −1.
 */
export function economicRival(state: StrategicState, sys: number, faction: number): number {
  if (!contestPair(state, sys)) return -1
  const other = pairA === faction ? pairB : pairB === faction ? pairA : -1
  if (other < 0 || !inLadder(state, faction) || !inLadder(state, other)) return -1
  return relationOf(state, faction, other) >= RELATION.economic ? other : -1
}

/** True when `stationFaction`'s stations undercut in `sys`: an economic rival there, and it is not the holder. */
export function undercuts(state: StrategicState, stationFaction: number, sys: number): boolean {
  if (economicRival(state, sys, stationFaction) < 0) return false
  return state.claims.holder[sys] !== stationFaction
}

/**
 * The undercut multiplier on a station's unit price for a trader's `side` (SIDE, the trader's point of view):
 * `economic.buyMult` when buying from an undercutting station, `economic.sellMult` when selling to it, else 1.
 */
export function undercutMult(state: StrategicState, stationFaction: number, sys: number, side: number): number {
  if (!undercuts(state, stationFaction, sys)) return 1
  const e = state.data.charter.ladder.economic
  return side === SIDE.buy ? e.buyMult : e.sellMult
}

/**
 * Supply denial (M5 spec §8.2): true when an agent of `agentFaction` may not trade at a station of
 * `stationFaction` in `sys` (the two are economic rivals contesting `sys`; both directions).
 */
export function denies(state: StrategicState, agentFaction: number, stationFaction: number, sys: number): boolean {
  if (agentFaction === stationFaction) return false
  return economicRival(state, sys, agentFaction) === stationFaction
}

// ---------------------------------------------------------------------------------------------
// Relations
// ---------------------------------------------------------------------------------------------

/**
 * The relation after one step from `current` at pressure `p` (hysteresis, M5 spec §8.1): climbs while
 * `p ≥ up` of the next level (war only when `warAllowed`), falls while `p < down` of the current level (war
 * only once `warDays ≥ war.minDays`). Pure.
 */
export function relationFor(state: StrategicState, current: number, p: number, warAllowed: boolean, warDays: number): number {
  const r = state.data.charter.ladder.relations
  const cfg = state.data.charter.ladder.war
  let level = current
  for (;;) {
    const next = level + 1
    if (next > RELATION.war) break
    const up = next === RELATION.economic ? r.economic.up : next === RELATION.skirmish ? r.skirmish.up : r.war.up
    if (p < up || (next === RELATION.war && !warAllowed)) break
    level = next
  }
  if (level === RELATION.war && !warAllowed && current !== RELATION.war) level = RELATION.skirmish
  for (;;) {
    if (level === RELATION.peace) break
    const down = level === RELATION.economic ? r.economic.down : level === RELATION.skirmish ? r.skirmish.down : r.war.down
    if (p >= down) break
    if (level === RELATION.war && current === RELATION.war && warDays < cfg.minDays) break
    level--
  }
  return level
}

/** Both treasuries at or above `war.minTreasury`. */
function canAffordWar(state: StrategicState, a: number, b: number): boolean {
  const min = state.data.charter.ladder.war.minTreasury
  const t = state.factions.treasury
  return t[a] >= min && t[b] >= min
}

/**
 * Sets the pair's relation, emits `relationChanged` and runs the war bookkeeping. No-op when unchanged. On a war
 * start `onWarStart` runs before the emit and its return is the event's `qty` (M10 P4a3a; 0 without a hook).
 */
function setRelation(state: StrategicState, a: number, b: number, to: number, onWarEnd: WarEndHook | null, onWarStart: WarStartHook | null): void {
  const rel = state.relations
  const fc = rel.factionCapacity
  const from = rel.relation[a * fc + b]
  if (from === to) return
  const lo = a < b ? a : b
  const hi = a < b ? b : a
  setPair(rel.relation, fc, lo, hi, to)
  setPair(rel.relationSince, fc, lo, hi, state.day)
  if (to === RELATION.war || from === RELATION.war) {
    rel.warLossValue[lo * fc + hi] = 0
    rel.warLossValue[hi * fc + lo] = 0
    setPair(rel.weariness, fc, lo, hi, 0)
  }
  if (from === RELATION.war) {
    setPair(rel.warEndDay, fc, lo, hi, state.day)
    const settle = state.data.charter.ladder.pressure.afterWar
    if (rel.pressure[lo * fc + hi] > settle) setPair(rel.pressure, fc, lo, hi, settle)
  }
  const cancelled = to === RELATION.war && from !== RELATION.war && onWarStart !== null ? onWarStart(state, lo, hi) : 0
  emit(state.events, EV.relationChanged, state.time, 0, -1, -1, lo, hi, -1, cancelled, rel.pressure[lo * fc + hi], to, from)
  if (from === RELATION.war && onWarEnd !== null) onWarEnd(state, lo, hi)
}

/** War weariness of the pair (0..1): the larger side's war losses ÷ its treasury. */
function wearinessOf(state: StrategicState, a: number, b: number): number {
  const rel = state.relations
  const fc = rel.factionCapacity
  const t = state.factions.treasury
  const la = rel.warLossValue[a * fc + b]
  const lb = rel.warLossValue[b * fc + a]
  const wa = t[a] > 0 ? la / t[a] : la > 0 ? 1 : 0
  const wb = t[b] > 0 ? lb / t[b] : lb > 0 ? 1 : 0
  const w = wa > wb ? wa : wb
  return w > 1 ? 1 : w
}

/** Counts contests and claim flips per pair into the scratch arrays. */
function countContestsAndFlips(state: StrategicState, fc: number): void {
  contests.fill(0)
  flips.fill(0)
  for (let s = 0; s < state.claims.systems; s++) {
    if (contestPair(state, s)) contests[pairA * fc + pairB]++
  }
  // Today's flips only: every claim change comes from the claims slice (1), before this one (3). The window is the
  // faction day, not the last dayS seconds: a coarse step runs the slices at the day boundary (event time = day
  // start), so a trailing window taken right after a fine run (the catch-up's first day) also counted the last fine
  // day's flips, stamped a tick or two into that day, a second time (fine vs coarse equivalence).
  const ring = state.notable
  const dayStart = state.day * state.data.strategic.clock.dayS
  for (let i = firstReadable(ring, 0); i < ring.head; i++) {
    const k = eventSlot(ring, i)
    if (ring.type[k] !== EV.claimChanged || ring.t[k] < dayStart) continue
    const x = ring.faction[k]
    const y = ring.other[k]
    if (x >= fc || y >= fc || x === y) continue
    flips[(x < y ? x : y) * fc + (x < y ? y : x)]++
  }
}

/**
 * Day slice 3: updates every ladder pair's pressure, weariness and relation (M5 spec §8.1), then clears the
 * day's kill counts. `onWarEnd` runs for each pair that leaves war (capture, C9 `war.ts`); `onWarStart` for each
 * pair that enters it (the war-start order cancellation, M10 P4a3a). Returns the number of relation changes.
 */
export function stepRelations(state: StrategicState, onWarEnd: WarEndHook | null = null, onWarStart: WarStartHook | null = null): number {
  const rel = state.relations
  const fc = rel.factionCapacity
  scratch(fc)
  countContestsAndFlips(state, fc)
  const cfg = state.data.charter.ladder.pressure
  const personality = state.data.charter.personality
  const start = state.factionAi.startTreasury
  const t = state.factions.treasury
  const nf = state.data.charter.factionCount < fc ? state.data.charter.factionCount : fc
  let changes = 0
  for (let a = 0; a < nf; a++) {
    if (!inLadder(state, a)) continue
    for (let b = a + 1; b < nf; b++) {
      if (!inLadder(state, b)) continue
      const k = a * fc + b
      const nContest = contests[k]
      let p = rel.pressure[k]
      p += cfg.perContest * nContest
      p += cfg.claimFlip * flips[k]
      const current = rel.relation[k]
      if (cfg.killsAtWar || current !== RELATION.war) {
        const kills = cfg.perKill * (rel.killsToday[k] + rel.killsToday[b * fc + a])
        p += kills < cfg.killCapPerDay ? kills : cfg.killCapPerDay
      }
      if (nContest > 0) {
        p += (cfg.conflictMult * (personality[a * TRAIT_COUNT + TRAIT.conflict] + personality[b * TRAIT_COUNT + TRAIT.conflict])) / 2
      }
      if (t[a] < cfg.lowTreasuryShare * start[a] || t[b] < cfg.lowTreasuryShare * start[b]) p += cfg.lowTreasury
      if (current === RELATION.war) {
        const w = wearinessOf(state, a, b)
        setPair(rel.weariness, fc, a, b, w)
        p += cfg.weariness * w
        p -= cfg.warDecayPerDay
      }
      p -= cfg.decayPerDay
      if (p < 0) p = 0
      if (p > cfg.max) p = cfg.max
      // M7 (spec §12.3, plan S17): an active grievance term holds P(a,b) at its floor after the decay.
      const g = grievanceFloor(state, a, b)
      if (p < g) p = g
      setPair(rel.pressure, fc, a, b, p)
      const warAllowed = nContest > 0 && canAffordWar(state, a, b)
      const next = relationFor(state, current, p, warAllowed, state.day - rel.relationSince[k])
      if (next !== current) {
        setRelation(state, a, b, next, onWarEnd, onWarStart)
        changes++
      }
    }
  }
  rel.killsToday.fill(0)
  return changes
}

/**
 * Dev forcing (`forceRelation`, M5 spec §16): sets the pair's relation now and its pressure to the level's
 * `up` threshold (peace: 0), so hysteresis holds it. Refused (false) for pairs outside the ladder.
 */
export function forceRelation(
  state: StrategicState,
  a: number,
  b: number,
  relation: number,
  onWarEnd: WarEndHook | null = null,
  onWarStart: WarStartHook | null = null,
): boolean {
  if (a === b || !inLadder(state, a) || !inLadder(state, b)) return false
  const r = state.data.charter.ladder.relations
  const p = relation === RELATION.war ? r.war.up : relation === RELATION.skirmish ? r.skirmish.up : relation === RELATION.economic ? r.economic.up : 0
  const rel = state.relations
  setPair(rel.pressure, rel.factionCapacity, a, b, p)
  setRelation(state, a, b, relation, onWarEnd, onWarStart)
  return true
}

/** True when any ladder pair is at `relation` or worse (a cheap guard for the per-round war work). */
export function anyAtLeast(state: StrategicState, relation: number): boolean {
  const rel = state.relations
  const fc = rel.factionCapacity
  const nf = state.data.charter.factionCount < fc ? state.data.charter.factionCount : fc
  for (let a = 0; a < nf; a++) for (let b = a + 1; b < nf; b++) if (rel.relation[a * fc + b] >= relation) return true
  return false
}
